/**
 * Vue I18n is an internationalization plugin for Vue.js. It easily integrates localization features.
 *
 * This file creates a vue-i18n instance with the following configuration:
 *    const i18n = createI18n({
 *      locale: 'en', // set locale
 *      messages, // set locale messages
 *    })
 *
 * Usage:
 *
 * In Vue components, you can now use the t method to translate your application.
 * Template example:
 *    <p>{{ t('foo.bar') }}</p>
 *
 *    <MyComponent :label="t('foo.bar')" />
 *
 * Script example using Composition API:
 *    import { useI18n } from 'vue-i18n'
 *    const { t } = useI18n()
 *    const label = t('foo.bar')
 *
 * You can change the locale using the locale.value property:
 *    const { locale } = useI18n()
 *    locale.value = 'fr'
 *
 * The translation messages are located in the src/i18n//locales directory, organized by language and namespace.
 *
 * Environment-Specific Behavior:
 * - In development mode: Uses local JSON files from src/i18n/locales
 * - In production mode: Fetches translations from Locize CDN: https://api.locize.app/{LOCIZE_PROJECT_ID}/latest/{lang}/{namespace}
 *   where multiple namespaces are fetched based on the local JSON structure
 *
 * Translation Overrides:
 * - Special namespaces (like 'bottimmo') are used to override base translations
 * - Overrides are applied after fetching all namespaces
 *
 * For more information, check out the official Vue I18n documentation: https://vue-i18n.intlify.dev/
 */
import * as Sentry from '@sentry/vue'
import { createI18n } from 'vue-i18n'

import deepMergeObjects from '@/lib/deepMerge'
import features, { appBranding } from '@/lib/features'

// Environment variables
const isDevMode = !import.meta.env.PROD
const LOCIZE_PROJECT_ID = import.meta.env.VITE_LOCIZE_PROJECT_ID

// Validate required environment variables in production
if (!isDevMode && !LOCIZE_PROJECT_ID) {
  Sentry.captureMessage('Missing VITE_LOCIZE_PROJECT_ID environment variable', {
    level: 'error',
    tags: { component: 'i18n' },
  })
}

// Import translations using Vite's glob import for development mode
// This will import all JSON files from all language directories
const translations = import.meta.glob('./locales/**/*.json', {
  eager: true,
  import: 'default',
})

// Process translations into messages object and detect available languages
const messages = {}

// Regular expression to match the language and namespace from the path
// Format: ./locales/{lang}/{namespace}.json
const pathRegex = /\.\/locales\/([^/]+)\/([^/]+)\.json$/

// Track all namespaces and overrides separately
const allNamespaces = new Set()
// List all potential override namespaces
const overrideNamespaces = ['bottimmo', 'smg', 'remax']

// Platform-specific override namespaces to actually apply
const activeOverrideNamespaces = []

// Check platform from appBranding and set up appropriate override namespaces
if (appBranding?.config?.platform === 'bottimmo') {
  activeOverrideNamespaces.push('bottimmo')
} else if (appBranding?.config?.platform === 'smg') {
  // For SMG, add both overrides with SMG taking precedence (order matters)
  activeOverrideNamespaces.push('bottimmo', 'smg')
} else if (appBranding?.config?.platform === 'remax') {
  // For REMAX, add remax override with highest precedence
  activeOverrideNamespaces.push('bottimmo', 'remax')
}
// No overrides for markero platform

/**
 * Get local translations
 * @param {string} lang The language code
 * @param {string} namespace The namespace
 * @returns {Object|null} Translation object or null if not available
 */
function getLocalTranslations(lang, namespace) {
  const path = `./locales/${lang}/${namespace}.json`
  if (translations[path]) {
    return translations[path]
  }
  return null
}

// Process translations from glob imports (used in development mode)
Object.entries(translations).forEach(([path, translation]) => {
  const match = path.match(pathRegex)
  if (match) {
    const [, lang, namespace] = match

    // Initialize language object if not exists
    if (!messages[lang]) {
      messages[lang] = {}
    }

    // Track this namespace for later use in production mode
    allNamespaces.add(namespace)

    // If it's an override namespace, store it separately
    if (overrideNamespaces.includes(namespace)) {
      // We'll handle overrides separately
      return
    }

    // Store each namespace as its own object to maintain namespace structure
    messages[lang][namespace] = translation
  }
})

// Get the list of available languages from the detected translations
const availableLocales = Object.keys(messages)

const STORAGE_KEY = 'appLocale'

/**
 * Retrieves the locale from localStorage
 * @returns {string|null} The stored locale or null if not found
 */
function getLocaleFromStorage() {
  return localStorage.getItem(STORAGE_KEY)
}

/**
 * Saves the locale to localStorage
 * @param {string} locale The locale to store
 */
function saveLocaleToStorage(locale) {
  localStorage.setItem(STORAGE_KEY, locale)
}

/**
 * Gets the locale from browser settings if it matches available locales
 * @returns {string|null} The matched locale or null if none found
 */
function getBrowserLocale() {
  for (const browserLocale of window.navigator.languages) {
    if (availableLocales.includes(browserLocale)) {
      return browserLocale
    } else {
      const language = browserLocale.split('-')[0]
      if (availableLocales.includes(language)) {
        return language
      }
    }
  }
  return null
}

/**
 * Gets the default locale from features config or falls back to 'de'
 * @returns {string} The default locale
 */
function getDefaultLocale() {
  return features?.i18n?.config?.defaultLocale || 'de'
}

/**
 * Gets the appropriate fallback locale for a given locale
 * This handles locale variants like de-CH -> de, en-US -> en, etc.
 * @param {string} locale The locale to get fallback for
 * @returns {string|string[]} The fallback locale(s)
 */
function getFallbackLocale(locale) {
  const defaultLocale = getDefaultLocale()

  // If the locale contains a hyphen (e.g., de-CH, en-US), extract the base language
  if (locale.includes('-')) {
    const baseLanguage = locale.split('-')[0]

    // If the base language is available in our translations, use it as fallback
    if (availableLocales.includes(baseLanguage)) {
      // Return array of fallbacks: base language first, then default locale
      const fallbacks =
        baseLanguage !== defaultLocale ? [baseLanguage, defaultLocale] : [baseLanguage]
      return fallbacks
    }
  }

  // For locales without variants or when base language isn't available, use default
  return defaultLocale
}

/**
 * Determines the client locale based on storage, browser settings, or default
 * @returns {string} The determined locale
 */
function getClientLocale() {
  const selectedLocale = getLocaleFromStorage()
  if (selectedLocale && availableLocales.includes(selectedLocale)) {
    return selectedLocale
  }

  const browserLocale = getBrowserLocale()
  if (browserLocale) {
    return browserLocale
  }

  return getDefaultLocale()
}

// Merge all namespaces for each language into a single object
// This maintains compatibility with the previous setup that used a single file per language
const mergedMessages = {}
Object.entries(messages).forEach(([lang, namespaceObjects]) => {
  // Initialize the language object if it doesn't exist
  if (!mergedMessages[lang]) {
    mergedMessages[lang] = {}
  }

  // Merge all namespace objects into a single object for this language
  Object.entries(namespaceObjects).forEach(([, translations]) => {
    mergedMessages[lang] = deepMergeObjects(mergedMessages[lang], translations)
  })
})

// Keep track of loading locales to prevent redundant network requests
const loadingLocales = new Map() // lang -> Promise

const i18nInstance = createI18n({
  locale: getClientLocale(),
  fallbackLocale: getFallbackLocale(getClientLocale()),
  messages: isDevMode ? mergedMessages : {}, // In development, use mergedMessages, in production, start with empty messages until loaded from CDN
  legacy: false, // Enable Composition API mode
  missingWarn: false, // Disable warnings about missing translations
  fallbackWarn: false, // Disable warnings about fallback usage
})

/**
 * Fetches translations from Locize CDN for a specific language and namespace
 * @param {string} lang The language to fetch translations for
 * @param {string} namespace The namespace to fetch
 * @returns {Promise<Object>} The translation object
 */
async function fetchLocizeNamespace(lang, namespace) {
  try {
    const response = await fetch(
      `https://api.locize.app/${LOCIZE_PROJECT_ID}/${import.meta.env.VITE_LOCIZE_VERSION || 'production'}/${lang}/${namespace}`
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch namespace ${namespace} for ${lang}: ${response.status}`)
    }
    const data = await response.json()

    // Locize returns data in the format { "namespace": { actual translations } }
    // We need to extract the actual translations
    if (data[namespace]) {
      return data[namespace]
    } else {
      return data // Return the full response as a fallback
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'i18n' },
      extra: { namespace, lang, action: 'fetchLocizeNamespace' },
    })
    // Fall back to local translations if available
    const localTranslations = getLocalTranslations(lang, namespace)
    return localTranslations || {}
  }
}

/**
 * Fetches all namespaces for a language from Locize CDN
 * @param {string} lang The language to fetch translations for
 * @returns {Promise<Object>} The merged translation object
 */
async function fetchLocizeTranslations(lang) {
  try {
    // Create an array of namespace names from the Set, excluding override namespaces
    const namespaces = [...allNamespaces].filter((ns) => !overrideNamespaces.includes(ns))

    // Fetch all regular namespaces in parallel
    const namespacePromises = namespaces.map((namespace) =>
      fetchLocizeNamespace(lang, namespace).then((data) => ({ namespace, data }))
    )

    // Also fetch the override namespaces in parallel
    const overridePromises = overrideNamespaces.map((namespace) =>
      fetchLocizeNamespace(lang, namespace).then((data) => ({ namespace, data }))
    )

    // Combine all promises
    const results = await Promise.all([...namespacePromises, ...overridePromises])

    // Merge all namespace results into a single object
    const mergedTranslations = {}
    let hasValidTranslations = false

    results.forEach(({ namespace, data }) => {
      if (Object.keys(data).length > 0) {
        mergedTranslations[namespace] = data
        hasValidTranslations = true
      } else {
        Sentry.addBreadcrumb({
          category: 'i18n',
          message: `Empty data received for namespace ${namespace}`,
          level: 'warning',
        })
        // Fall back to local translations if remote data is empty
        const localTranslations = getLocalTranslations(lang, namespace)
        if (localTranslations) {
          mergedTranslations[namespace] = localTranslations
          hasValidTranslations = true
        }
      }
    })

    // If we couldn't get any valid translations from the API, fall back completely to local translations
    if (!hasValidTranslations && !isDevMode) {
      Sentry.captureMessage(
        `Falling back to local translations for ${lang} due to empty or failed remote data`,
        {
          level: 'warning',
          tags: { component: 'i18n' },
        }
      )
      return getFallbackLocalTranslations(lang)
    }

    return mergedTranslations
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'i18n' },
      extra: { lang, action: 'fetchLocizeTranslations' },
    })
    // Fall back to local translations in case of error
    return getFallbackLocalTranslations(lang)
  }
}

/**
 * Gets local translations as a fallback when remote fetching fails
 * @param {string} lang The language to get local translations for
 * @returns {Object} The merged local translations
 */
function getFallbackLocalTranslations(lang) {
  const localTranslations = {}

  // Process all local translation files for this language
  Object.entries(translations).forEach(([path, translation]) => {
    const match = path.match(pathRegex)
    if (match && match[1] === lang) {
      const namespace = match[2]
      // Include all namespaces, both regular and override namespaces
      localTranslations[namespace] = translation
    }
  })

  Sentry.addBreadcrumb({
    category: 'i18n',
    message: `Using local translations for ${lang}`,
    level: 'info',
  })
  return localTranslations
}

/**
 * Loads and applies translation overrides asynchronously
 * @param {string} lang The language to apply overrides for
 * @param {Object} existingTranslations Already fetched translations to use instead of re-fetching
 */
async function loadOverrides(lang, existingTranslations = null) {
  try {
    // First, merge all namespaces into a single object
    const baseTranslations = {}
    const overrideTranslationsMap = {}

    // If we already have translations fetched, use them instead of re-fetching
    if (existingTranslations && Object.keys(existingTranslations).length > 0) {
      // First process regular namespaces
      Object.entries(existingTranslations).forEach(([namespace, translations]) => {
        if (!overrideNamespaces.includes(namespace)) {
          baseTranslations[namespace] = translations
        } else if (activeOverrideNamespaces.includes(namespace)) {
          // Only store active override namespaces separately
          overrideTranslationsMap[namespace] = translations
        }
      })
    } else {
      // Process all regular namespaces first
      const regularNamespaces = [...allNamespaces].filter((ns) => !overrideNamespaces.includes(ns))

      for (const namespace of regularNamespaces) {
        if (!isDevMode) {
          const translations = await fetchLocizeNamespace(lang, namespace)
          if (Object.keys(translations).length > 0) {
            baseTranslations[namespace] = translations
          }
        } else {
          const localTranslations = getLocalTranslations(lang, namespace)
          if (localTranslations) {
            baseTranslations[namespace] = localTranslations
          }
        }
      }

      // Fetch only active override namespaces separately
      for (const namespace of activeOverrideNamespaces) {
        let override
        if (!isDevMode) {
          override = await fetchLocizeNamespace(lang, namespace)

          // Fall back to local translations if remote override is empty
          if (!override || Object.keys(override).length === 0) {
            override = getLocalTranslations(lang, namespace)
          }
        } else {
          override = getLocalTranslations(lang, namespace)
        }

        if (override && Object.keys(override).length > 0) {
          overrideTranslationsMap[namespace] = override
        }
      }
    }

    // Create the final translations object by first flattening base translations
    let finalTranslations = {}

    // Special handling for development mode - we need to flatten the namespaces
    if (isDevMode) {
      // First, merge all regular namespace objects into a flattened structure
      Object.values(baseTranslations).forEach((nsTranslations) => {
        Object.keys(nsTranslations).forEach((key) => {
          if (typeof nsTranslations[key] === 'object' && nsTranslations[key] !== null) {
            finalTranslations[key] = finalTranslations[key] || {}
            finalTranslations[key] = deepMergeObjects(finalTranslations[key], nsTranslations[key])
          } else {
            finalTranslations[key] = nsTranslations[key]
          }
        })
      })

      // Now apply all overrides on top of the flattened structure
      Object.values(overrideTranslationsMap).forEach((overrideTranslations) => {
        Object.keys(overrideTranslations).forEach((key) => {
          if (typeof overrideTranslations[key] === 'object' && overrideTranslations[key] !== null) {
            finalTranslations[key] = finalTranslations[key] || {}
            // For objects, we need to merge them, with override values taking precedence
            finalTranslations[key] = deepMergeObjects(
              finalTranslations[key],
              overrideTranslations[key]
            )
          } else {
            // For primitives, simply override
            finalTranslations[key] = overrideTranslations[key]
          }
        })
      })

      i18nInstance.global.setLocaleMessage(lang, finalTranslations)
    } else {
      // In production mode, keep the namespace structure but ensure overrides are also applied
      // Copy base translations to final
      finalTranslations = { ...baseTranslations }

      // Apply overrides
      Object.entries(overrideTranslationsMap).forEach(([, overrideTranslations]) => {
        // For each override namespace, extract its keys and apply them to the final translations
        Object.keys(overrideTranslations).forEach((key) => {
          if (typeof overrideTranslations[key] === 'object' && overrideTranslations[key] !== null) {
            // If the key exists in final translations, merge them with override taking precedence
            if (finalTranslations[key]) {
              finalTranslations[key] = deepMergeObjects(
                finalTranslations[key],
                overrideTranslations[key]
              )
            } else {
              // If the key doesn't exist, just add it
              finalTranslations[key] = overrideTranslations[key]
            }
          } else {
            // For primitives, simply override
            finalTranslations[key] = overrideTranslations[key]
          }
        })
      })

      i18nInstance.global.setLocaleMessage(lang, finalTranslations)
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'i18n' },
      extra: { lang, action: 'loadOverrides' },
    })
  }
}

/**
 * Loads translations for the current locale only
 * We no longer preload all locales on startup to improve performance
 * This is a key optimization: only load what's needed immediately
 * Returns a promise that resolves when the translations are loaded
 */
async function loadCurrentLocaleTranslations() {
  const currentLocale = i18nInstance.global.locale.value

  // If this locale is already being loaded, return the existing promise
  if (loadingLocales.has(currentLocale)) {
    return loadingLocales.get(currentLocale)
  }

  // Create a new promise for this locale load
  const loadPromise = (async () => {
    try {
      if (!isDevMode) {
        // Fetch translations for the current locale
        const translations = await fetchLocizeTranslations(currentLocale)

        // If we have fetched translations (potentially using local fallbacks)
        if (translations && Object.keys(translations).length > 0) {
          // Apply the translations and also apply overrides, reusing the fetched translations
          await loadOverrides(currentLocale, translations)
        } else {
          Sentry.captureMessage(`No translations available for ${currentLocale}`, {
            level: 'error',
            tags: { component: 'i18n' },
          })
        }
      } else {
        // In development mode, we need to ensure translations are fully applied
        await loadOverrides(currentLocale)
      }

      // Also ensure fallback locale translations are loaded
      await ensureFallbackLocalesLoaded(currentLocale)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'i18n' },
        extra: { locale: currentLocale, action: 'loadCurrentLocaleTranslations' },
      })

      // Remove from loading map on error so it can be retried
      loadingLocales.delete(currentLocale)
      throw error
    }
  })()

  // Store the promise in the map
  loadingLocales.set(currentLocale, loadPromise)

  return loadPromise
}

/**
 * Ensures that fallback locale translations are loaded
 * @param {string} locale The primary locale
 */
async function ensureFallbackLocalesLoaded(locale) {
  const fallbackLocales = getFallbackLocale(locale)
  const fallbackArray = Array.isArray(fallbackLocales) ? fallbackLocales : [fallbackLocales]

  // Load fallback locales that aren't already loaded
  for (const fallbackLocale of fallbackArray) {
    if (fallbackLocale !== locale && !i18nInstance.global.messages.value[fallbackLocale]) {
      try {
        if (!isDevMode) {
          // Fetch translations for the fallback locale
          const translations = await fetchLocizeTranslations(fallbackLocale)
          if (translations && Object.keys(translations).length > 0) {
            await loadOverrides(fallbackLocale, translations)
          }
        } else {
          // In development mode, apply overrides with local translations
          await loadOverrides(fallbackLocale)
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'i18n' },
          extra: { locale: fallbackLocale, action: 'ensureFallbackLocalesLoaded' },
        })
      }
    }
  }
}

// Start loading translations for current locale only
loadCurrentLocaleTranslations()

const i18n = {
  availableLocales,

  /**
   * Returns the i18n instance.
   * @returns {Object} i18n instance
   */
  getInstance() {
    return i18nInstance
  },

  /**
   * Changes the locale of the i18n instance and saves it to the local storage.
   * In production mode, also fetches the translations for the new locale if not already loaded.
   * This implements the on-demand loading strategy: only fetch translations when needed.
   * @param {string} locale The new locale to set
   */
  async changeLocale(locale) {
    // Save to localStorage
    saveLocaleToStorage(locale)

    // Update fallback locale for the new locale
    i18nInstance.global.fallbackLocale.value = getFallbackLocale(locale)

    // If this locale is already being loaded, wait for it to complete
    if (loadingLocales.has(locale)) {
      await loadingLocales.get(locale)
    } else {
      // Production mode: fetch from CDN
      const loadPromise = (async () => {
        try {
          if (!isDevMode) {
            // First fetch the base translations
            const translations = await fetchLocizeTranslations(locale)

            // If we have fetched translations (potentially using local fallbacks)
            if (translations && Object.keys(translations).length > 0) {
              // Apply translations and overrides in one step, reusing the fetched translations
              await loadOverrides(locale, translations)
            } else {
              Sentry.captureMessage(`No translations available for ${locale}`, {
                level: 'error',
                tags: { component: 'i18n' },
              })
            }
          } else {
            // Development mode: apply overrides with local translations
            await loadOverrides(locale)
          }

          // Also ensure fallback locale translations are loaded
          await ensureFallbackLocalesLoaded(locale)
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'i18n' },
            extra: { locale, action: 'changeLocale' },
          })

          // Remove from loading map on error so it can be retried
          loadingLocales.delete(locale)
          throw error
        }
      })()

      // Store the promise in the map
      loadingLocales.set(locale, loadPromise)

      // Wait for loading to complete
      await loadPromise
    }

    // Update the locale value after loading is complete
    i18nInstance.global.locale.value = locale
  },
}

export default i18n
